import { logPerfAction } from "./logPerfAction";

export type PerfLogPayload = {
  /** 产品线：ehub3 / official / mall / community */
  product?: string;
  eventName: string;
  durationMs: number;
  success?: boolean;
  deviceType?: string;
  connectionType?: "USB" | "2.4G";
  deviceModel?: string;
  moduleId?: string;
  clientScope?: "web" | "desktop";
  appVersion?: string;
  userId?: number;
  sessionId?: string;
  extra?: Record<string, unknown>;
};

const SESSION_KEY = "EVENT_LOG_SESSION_ID";
const ROUTE_PENDING_KEY = "PERF_ROUTE_PENDING";

const getOrCreateSessionId = () => {
  if (typeof localStorage === "undefined") return "";
  let sessionId = localStorage.getItem(SESSION_KEY) || "";
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
};

const parseUserId = (): number | undefined => {
  try {
    const raw = localStorage.getItem("userInfo");
    const userInfo = raw ? JSON.parse(raw) : null;
    const idVal = userInfo?.id;
    if (typeof idVal === "number") return idVal;
    if (typeof idVal === "string" && idVal.trim() !== "") {
      const parsed = Number(idVal);
      if (!Number.isNaN(parsed)) return parsed;
    }
  } catch {
    // ignore
  }
  return undefined;
};

const detectClientScope = (): "web" | "desktop" => {
  if (typeof window === "undefined") return "web";
  if ((window as any).electronBridge || (window as any).electronAPI) {
    return "desktop";
  }
  if (
    typeof navigator !== "undefined" &&
    /electron/i.test(navigator.userAgent)
  ) {
    return "desktop";
  }
  return "web";
};

/** 上报性能埋点（失败静默） */
export function logPerf(payload: PerfLogPayload): void {
  if (typeof fetch === "undefined") return;
  const body = {
    product: payload.product ?? "ehub3",
    eventName: payload.eventName,
    durationMs: Math.max(0, Math.round(payload.durationMs)),
    success: payload.success !== false,
    deviceType: payload.deviceType,
    connectionType: payload.connectionType,
    deviceModel: payload.deviceModel,
    moduleId: payload.moduleId,
    clientScope: payload.clientScope ?? detectClientScope(),
    appVersion: payload.appVersion,
    userId: payload.userId ?? parseUserId(),
    sessionId: payload.sessionId ?? getOrCreateSessionId(),
    extra: payload.extra ? JSON.stringify(payload.extra) : undefined,
  };
  void fetch("/api/v1/perf-logs", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).catch(() => {});
}

/** 侧边栏菜单切换：记录开始时间 */
export function markRouteSwitchStart(
  targetPath: string,
  product: string = "ehub3"
) {
  if (typeof sessionStorage === "undefined") return;
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const prefix = pathname.includes("keyboard-management")
    ? "/keyboard-management/"
    : pathname.includes("mouse-management")
      ? "/mouse-management/"
      : "";
  const fullTarget = targetPath.startsWith("/")
    ? targetPath
    : `${prefix}${targetPath}`;
  sessionStorage.setItem(
    ROUTE_PENDING_KEY,
    JSON.stringify({
      targetPath: fullTarget,
      startMs: Date.now(),
      product,
    })
  );
}

const CONNECT_PENDING_KEY = "PERF_CONNECT_PENDING";

export type ConnectDevicePerfMeta = {
  deviceType?: string;
  connectionType?: "USB" | "2.4G";
  deviceModel?: string;
  extra?: Record<string, unknown>;
};

/** 用户点击连接设备：记录开始时间 */
export function markConnectDeviceStart(meta: ConnectDevicePerfMeta = {}) {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(
    CONNECT_PENDING_KEY,
    JSON.stringify({
      startMs: Date.now(),
      ...meta,
    })
  );
}

/** 连接成功/失败后上报 connect_device */
export function completeConnectDevicePerf(
  result: {
    success: boolean;
    reason?: string;
  } & ConnectDevicePerfMeta
) {
  if (typeof sessionStorage === "undefined") return;
  const raw = sessionStorage.getItem(CONNECT_PENDING_KEY);
  sessionStorage.removeItem(CONNECT_PENDING_KEY);
  if (!raw) return;
  try {
    const pending = JSON.parse(raw) as {
      startMs?: number;
    } & ConnectDevicePerfMeta;
    const startMs = Number(pending.startMs);
    if (!Number.isFinite(startMs)) return;
    logPerf({
      product: "ehub3",
      eventName: "connect_device",
      durationMs: Date.now() - startMs,
      success: result.success,
      deviceType: result.deviceType ?? pending.deviceType,
      connectionType: result.connectionType ?? pending.connectionType,
      deviceModel: result.deviceModel ?? pending.deviceModel,
      moduleId: "choose-device/device-card",
      extra: {
        ...(pending.extra || {}),
        ...(result.extra || {}),
        ...(result.reason ? { reason: result.reason } : {}),
      },
    });
  } catch {
    // ignore
  }
}

/** 路由 VISIT 时尝试完结菜单切换耗时 */
export function tryCompleteRouteSwitchPerf(actualPath: string) {
  if (typeof sessionStorage === "undefined") return;
  const raw = sessionStorage.getItem(ROUTE_PENDING_KEY);
  if (!raw) return;
  try {
    const pending = JSON.parse(raw) as {
      targetPath?: string;
      startMs?: number;
      product?: string;
    };
    const target = String(pending.targetPath ?? "");
    const startMs = Number(pending.startMs);
    if (!target || !Number.isFinite(startMs)) return;
    const pathOnly = (actualPath || "").split("?")[0] || "";
    const matches =
      pathOnly === target ||
      pathOnly.endsWith(`/${target}`) ||
      pathOnly.includes(target);
    if (!matches) return;
    sessionStorage.removeItem(ROUTE_PENDING_KEY);
    const durationMs = Date.now() - startMs;
    const moduleId = pathOnly.replace(/^\//, "") || "home";
    const common = {
      product: pending.product ?? "ehub3",
      durationMs,
      success: true,
      moduleId,
    } as const;
    logPerf({ ...common, eventName: PERF_EVENT.ROUTE_SWITCH });
    // 与 route_switch 同一次计时双写，后管可单独展示「进入模块」
    logPerf({ ...common, eventName: PERF_EVENT.ENTER_MODULE });
    const segment = moduleId.includes("/")
      ? moduleId.slice(moduleId.lastIndexOf("/") + 1)
      : moduleId;
    logPerfAction({
      feature: "enter_module",
      actionId: `menu/${segment || "home"}`,
      actionLabel: "进入模块",
      moduleId,
      durationMs,
      success: true,
      deviceType: moduleId.startsWith("keyboard-management")
        ? "keyboard"
        : moduleId.startsWith("mouse-management")
          ? "mouse"
          : undefined,
    });
  } catch {
    sessionStorage.removeItem(ROUTE_PENDING_KEY);
  }
}

// ---------------------------------------------------------------------------
// 性能分析 v2：通用计时 / 进子应用 / SDK Proxy
// ---------------------------------------------------------------------------

export const PERF_EVENT = {
  CONNECT_DEVICE: "connect_device",
  ROUTE_SWITCH: "route_switch",
  DESKTOP_COLD_START: "desktop_cold_start",
  DESKTOP_WEBVIEW_READY: "desktop_webview_ready",
  ENTER_SUBAPP: "enter_subapp",
  ENTER_MODULE: "enter_module",
  VIRTUAL_KEYBOARD_LOAD: "virtual_keyboard_load",
  SWITCH_LIGHTING: "switch_lighting",
  CHANGE_KEY: "change_key",
  SDK_CALL: "sdk_call",
} as const;

export type PerfEventName = (typeof PERF_EVENT)[keyof typeof PERF_EVENT];

const GENERIC_PENDING_PREFIX = "PERF_GENERIC_PENDING:";
const ENTER_SUBAPP_PENDING_KEY = "PERF_ENTER_SUBAPP_PENDING";

export type MeasurePerfMeta = Omit<
  PerfLogPayload,
  "eventName" | "durationMs" | "success"
> & {
  success?: boolean;
  reason?: string;
};

/** 包裹任意 async，自动上报 durationMs */
export async function measurePerf<T>(
  eventName: string,
  fn: () => Promise<T>,
  meta: MeasurePerfMeta = {}
): Promise<T> {
  const startMs = Date.now();
  try {
    const result = await fn();
    logPerf({
      ...meta,
      eventName,
      durationMs: Date.now() - startMs,
      success: meta.success !== false,
      extra: {
        ...(meta.extra || {}),
        ...(meta.reason ? { reason: meta.reason } : {}),
      },
    });
    return result;
  } catch (err) {
    logPerf({
      ...meta,
      eventName,
      durationMs: Date.now() - startMs,
      success: false,
      extra: {
        ...(meta.extra || {}),
        reason:
          meta.reason ||
          (err instanceof Error ? err.message : "measure_perf_failed"),
      },
    });
    throw err;
  }
}

/** 跨异步边界：记录开始时间 */
export function markPerfStart(
  key: string,
  meta: Record<string, unknown> = {}
): void {
  if (typeof sessionStorage === "undefined" || !key) return;
  sessionStorage.setItem(
    `${GENERIC_PENDING_PREFIX}${key}`,
    JSON.stringify({ startMs: Date.now(), ...meta })
  );
}

/** 跨异步边界：完结并上报 */
export function completePerf(
  key: string,
  eventName: string,
  result: MeasurePerfMeta & { success?: boolean } = {}
): void {
  if (typeof sessionStorage === "undefined" || !key) return;
  const storageKey = `${GENERIC_PENDING_PREFIX}${key}`;
  const raw = sessionStorage.getItem(storageKey);
  sessionStorage.removeItem(storageKey);
  if (!raw) return;
  try {
    const pending = JSON.parse(raw) as {
      startMs?: number;
      [k: string]: unknown;
    };
    const { startMs: startMsRaw, ...pendingRest } = pending;
    const startMs = Number(startMsRaw);
    if (!Number.isFinite(startMs)) return;
    logPerf({
      product: "ehub3",
      eventName,
      durationMs: Date.now() - startMs,
      success: result.success !== false,
      deviceType:
        result.deviceType ?? (pendingRest.deviceType as string | undefined),
      connectionType:
        result.connectionType ??
        (pendingRest.connectionType as "USB" | "2.4G" | undefined),
      deviceModel:
        result.deviceModel ?? (pendingRest.deviceModel as string | undefined),
      moduleId: result.moduleId ?? (pendingRest.moduleId as string | undefined),
      extra: {
        ...(typeof pendingRest.extra === "object" && pendingRest.extra
          ? (pendingRest.extra as Record<string, unknown>)
          : {}),
        ...(result.extra || {}),
        ...(result.reason ? { reason: result.reason } : {}),
        ...(pendingRest.subApp ? { subApp: pendingRest.subApp } : {}),
      },
    });
  } catch {
    // ignore
  }
}

/** 连接成功、即将进入键鼠子应用时调用 */
export function markEnterSubappStart(meta: {
  subApp: "keyboard" | "mouse" | "earphone";
  deviceType?: string;
  deviceModel?: string;
  connectionType?: "USB" | "2.4G";
}) {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(
    ENTER_SUBAPP_PENDING_KEY,
    JSON.stringify({ startMs: Date.now(), ...meta })
  );
}

/** 子应用首个路由曝光时完结 enter_subapp */
export function tryCompleteEnterSubappPerf(actualPath: string) {
  if (typeof sessionStorage === "undefined") return;
  const raw = sessionStorage.getItem(ENTER_SUBAPP_PENDING_KEY);
  if (!raw) return;
  const pathOnly = (actualPath || "").split("?")[0] || "";
  const isKeyboard = pathOnly.includes("keyboard-management");
  const isMouse = pathOnly.includes("mouse-management");
  const isEarphone = pathOnly.includes("earphone-management");
  if (!isKeyboard && !isMouse && !isEarphone) return;
  try {
    const pending = JSON.parse(raw) as {
      startMs?: number;
      subApp?: string;
      deviceType?: string;
      deviceModel?: string;
      connectionType?: "USB" | "2.4G";
    };
    const expect = pending.subApp;
    if (expect === "keyboard" && !isKeyboard) return;
    if (expect === "mouse" && !isMouse) return;
    if (expect === "earphone" && !isEarphone) return;
    sessionStorage.removeItem(ENTER_SUBAPP_PENDING_KEY);
    const startMs = Number(pending.startMs);
    if (!Number.isFinite(startMs)) return;
    const durationMs = Date.now() - startMs;
    const moduleId = pathOnly.replace(/^\//, "") || "home";
    const subApp =
      pending.subApp ??
      (isKeyboard ? "keyboard" : isEarphone ? "earphone" : "mouse");
    const actionLabel =
      subApp === "keyboard"
        ? "进入键盘子应用"
        : subApp === "mouse"
          ? "进入鼠标子应用"
          : "进入耳机子应用";
    logPerf({
      product: "ehub3",
      eventName: PERF_EVENT.ENTER_SUBAPP,
      durationMs,
      success: true,
      deviceType: pending.deviceType,
      deviceModel: pending.deviceModel,
      connectionType: pending.connectionType,
      moduleId,
      extra: { subApp },
    });
    logPerfAction({
      feature: "enter_subapp",
      actionId: `subapp/${subApp}`,
      actionLabel,
      moduleId,
      durationMs,
      success: true,
      deviceType: pending.deviceType,
      deviceModel: pending.deviceModel,
      connectionType: pending.connectionType,
      extra: { subApp },
    });
  } catch {
    sessionStorage.removeItem(ENTER_SUBAPP_PENDING_KEY);
  }
}

/**
 * 同一路由下虚拟键盘加载只上报一次。
 * 进页常会并发触发 autoLoad + KEYBOARD_DEVICE_LAYOUT_REFRESH + 多处 useKeyboardLayout，
 * 若不去重会出现多对 perf-logs / perf-actions。
 * 切到其它 moduleId（其它页面）后自动放开，可再次上报。
 */
let lastVirtualKeyboardLoadModuleId: string | null = null;

/** 虚拟键盘布局加载耗时 */
export function logVirtualKeyboardLoadPerf(result: {
  durationMs: number;
  success: boolean;
  moduleId?: string;
  reason?: string;
  extra?: Record<string, unknown>;
}) {
  const moduleId = result.moduleId ?? "";
  if (moduleId && lastVirtualKeyboardLoadModuleId === moduleId) {
    return;
  }
  if (moduleId) {
    lastVirtualKeyboardLoadModuleId = moduleId;
  }
  logPerf({
    product: "ehub3",
    eventName: PERF_EVENT.VIRTUAL_KEYBOARD_LOAD,
    durationMs: result.durationMs,
    success: result.success,
    deviceType: "keyboard",
    moduleId: result.moduleId,
    extra: {
      ...(result.extra || {}),
      ...(result.reason ? { reason: result.reason } : {}),
    },
  });
  logPerfAction({
    feature: "virtual_keyboard",
    actionId: "layout/load",
    actionLabel: "虚拟键盘加载",
    moduleId: result.moduleId,
    durationMs: result.durationMs,
    success: result.success,
    deviceType: "keyboard",
    extra: {
      ...(result.extra || {}),
      ...(result.reason ? { reason: result.reason } : {}),
    },
  });
}

// --- SDK Proxy ---

const SDK_PERF_DENYLIST: RegExp[] = [
  /getBattery/i,
  /getDeviceStatus/i,
  /heartbeat/i,
  /ping/i,
  /sendMusic/i,
  /setLightBox/i,
  /musicRhythm/i,
  /setLightingCustom/i, // 律动逐帧；业务切灯效走 setLightingBase
];

const LIGHTING_METHOD_RE =
  /setLightingBase|setLightingPalette|setLightMode|setLightingSleep/i;
const CHANGE_KEY_METHOD_RE =
  /setRemap|setKeyInfo|setKeyData|writeKey|setKbKey|setKeyboardKey|keyRemap|setChangeKey|saveKey/i;

function shouldSkipSdkPerf(methodPath: string): boolean {
  return SDK_PERF_DENYLIST.some((re) => re.test(methodPath));
}

function resolveNamedEventForSdkMethod(methodPath: string): string | null {
  const leaf = methodPath.split(".").pop() || methodPath;
  if (LIGHTING_METHOD_RE.test(leaf) || LIGHTING_METHOD_RE.test(methodPath)) {
    return PERF_EVENT.SWITCH_LIGHTING;
  }
  if (
    CHANGE_KEY_METHOD_RE.test(leaf) ||
    CHANGE_KEY_METHOD_RE.test(methodPath)
  ) {
    return PERF_EVENT.CHANGE_KEY;
  }
  return null;
}

function currentModuleIdFromLocation(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return window.location.pathname.replace(/^\//, "") || undefined;
}

function wrapSdkFunction(
  fn: (...args: unknown[]) => unknown,
  methodPath: string
): (...args: unknown[]) => unknown {
  return function wrappedSdkFn(this: unknown, ...args: unknown[]) {
    if (shouldSkipSdkPerf(methodPath)) {
      return fn.apply(this, args);
    }
    // sdk_call 全量上报过于频繁，已关闭；仅保留灯效/改键等命名事件
    const named = resolveNamedEventForSdkMethod(methodPath);
    if (!named) {
      return fn.apply(this, args);
    }
    let result: unknown;
    try {
      result = fn.apply(this, args);
    } catch (err) {
      // logPerf({
      //   product: "ehub3",
      //   eventName: PERF_EVENT.SDK_CALL,
      //   durationMs: 0,
      //   success: false,
      //   moduleId: currentModuleIdFromLocation(),
      //   extra: {
      //     sdkMethod: methodPath,
      //     reason: err instanceof Error ? err.message : "sdk_sync_throw",
      //   },
      // });
      logPerf({
        product: "ehub3",
        eventName: named,
        durationMs: 0,
        success: false,
        moduleId: currentModuleIdFromLocation(),
        extra: {
          sdkMethod: methodPath,
          reason: err instanceof Error ? err.message : "sdk_sync_throw",
        },
      });
      throw err;
    }
    if (
      result == null ||
      typeof (result as Promise<unknown>).then !== "function"
    ) {
      return result;
    }
    const startMs = Date.now();
    return Promise.resolve(result).then(
      (value) => {
        const durationMs = Date.now() - startMs;
        const moduleId = currentModuleIdFromLocation();
        const extra = { sdkMethod: methodPath };
        // logPerf({
        //   product: "ehub3",
        //   eventName: PERF_EVENT.SDK_CALL,
        //   durationMs,
        //   success: true,
        //   moduleId,
        //   extra,
        // });
        logPerf({
          product: "ehub3",
          eventName: named,
          durationMs,
          success: true,
          moduleId,
          extra,
        });
        return value;
      },
      (err) => {
        const durationMs = Date.now() - startMs;
        const moduleId = currentModuleIdFromLocation();
        const extra = {
          sdkMethod: methodPath,
          reason: err instanceof Error ? err.message : "sdk_reject",
        };
        // logPerf({
        //   product: "ehub3",
        //   eventName: PERF_EVENT.SDK_CALL,
        //   durationMs,
        //   success: false,
        //   moduleId,
        //   extra,
        // });
        logPerf({
          product: "ehub3",
          eventName: named,
          durationMs,
          success: false,
          moduleId,
          extra,
        });
        throw err;
      }
    );
  };
}

function wrapSdkNode(
  node: unknown,
  path: string,
  seen: WeakSet<object>
): unknown {
  if (node == null) return node;
  if (typeof node === "function") {
    return wrapSdkFunction(
      node as (...args: unknown[]) => unknown,
      path || "fn"
    );
  }
  if (typeof node !== "object") return node;
  if (seen.has(node as object)) return node;
  seen.add(node as object);

  // 数组不包装元素
  if (Array.isArray(node)) return node;

  const source = node as Record<string, unknown>;
  // SDK 实例容器（serviceKeyboard / beiyingMachineKeyboard 等）必须保持同一引用：
  // connect 层写入 commonApi 单例，runtime getter 若拿到深拷贝会导致 instance 分裂。
  if (Object.prototype.hasOwnProperty.call(source, "instance")) {
    return node;
  }

  const out: Record<string, unknown> = {};
  for (const key of Object.keys(source)) {
    const childPath = path ? `${path}.${key}` : key;
    try {
      out[key] = wrapSdkNode(source[key], childPath, seen);
    } catch {
      out[key] = source[key];
    }
  }
  // 保留原型上少见方法：用 Proxy 兜底未拷贝到的访问
  return new Proxy(out, {
    get(target, prop, receiver) {
      if (typeof prop === "symbol") return Reflect.get(target, prop, receiver);
      if (prop in target) return Reflect.get(target, prop, receiver);
      const raw = Reflect.get(source, prop, source);
      const childPath = path ? `${path}.${String(prop)}` : String(prop);
      if (typeof raw === "function") {
        const wrapped = wrapSdkFunction(
          raw as (...args: unknown[]) => unknown,
          childPath
        );
        (target as Record<string, unknown>)[String(prop)] = wrapped;
        return wrapped;
      }
      return raw;
    },
  });
}

/**
 * 对 sdkApi 对象树做浅+深包装：
 * sdk_call 全量上报已关闭（过于频繁）；命中灯效/改键白名单时打 switch_lighting / change_key。
 * 同一实例只应 wrap 一次（loader 挂载点）。
 */
export function wrapSdkApiWithPerf<T extends object>(sdkApi: T): T {
  if (!sdkApi || typeof sdkApi !== "object") return sdkApi;
  try {
    return wrapSdkNode(sdkApi, "", new WeakSet()) as T;
  } catch {
    return sdkApi;
  }
}
